/**
 * Thin wrapper around the Microsoft Graph endpoints used by the SPA.
 * Intentionally tiny — no SDK; the auth context supplies a delegated
 * access token via getAccessToken(['User.ReadBasic.All']).
 *
 * Colleagues are NOT stored in Cloud Contacts. They live in Entra; we
 * read them on demand. The user can elect to copy a colleague into their
 * private Cloud Contacts entry if they want notes / call activity / etc.
 * against them.
 */
const GRAPH = 'https://graph.microsoft.com/v1.0';

export const COLLEAGUES_SCOPES = ['User.ReadBasic.All'];

export interface GraphColleague {
  id: string;
  displayName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  mail?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  userPrincipalName?: string;
}

interface ListResult {
  value: GraphColleague[];
  '@odata.nextLink'?: string;
}

const SELECT =
  'id,displayName,givenName,surname,jobTitle,department,mail,mobilePhone,businessPhones,userPrincipalName';

export async function listColleagues(token: string, query?: string): Promise<GraphColleague[]> {
  const params = new URLSearchParams({ $select: SELECT, $top: '50' });
  if (query?.trim()) {
    // ConsistencyLevel: eventual + $search header is the recommended way
    // for substring matches on Graph users.
    params.set('$search', `"displayName:${query.trim()}" OR "mail:${query.trim()}"`);
  }
  params.set('$orderby', 'displayName');

  const res = await fetch(`${GRAPH}/users?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ConsistencyLevel: 'eventual'
    }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Graph error ${res.status}: ${text.slice(0, 200)}`);
  }
  const body = (await res.json()) as ListResult;
  return body.value;
}

export async function getMyPhoto(token: string): Promise<string | null> {
  const res = await fetch(`${GRAPH}/me/photo/$value`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
